/**
 * DocumentInspector - the right panel shown when no section is selected: the
 * document-level controls, grouped into tabs. Brand (cover + logos, with the
 * inherited workspace color shown read-only and a link out to manage it), Page
 * chrome (header/footer/page numbers), Settings (title/font/margins/...), and
 * Data (the preview sample). Cover identity + workspace brand are *inherited* and
 * edited in Workspace Settings; the template only pins per-slot assets.
 */

import type { DocumentContract } from '@protoss/doc';
import type { VisualOverrides } from '@protoss/doc';
import { Button, SegmentedControl } from '@protoss/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { SowCoverBrandPanel, type SowWorkspaceBrand } from '../sow-template/SowCoverBrandPanel';
import { SowDocumentSettingsPanel } from '../sow-template/SowDocumentSettingsPanel';
import type { SowDocumentSettings } from '../../lib/sowContract';
import type { PageChrome } from '../../lib/sowPageChrome';
import { PageChromeEditor } from './PageChromeEditor';

type DocTab = 'brand' | 'page' | 'settings' | 'data';

export interface DocumentInspectorProps {
  draftContract: DocumentContract;
  assetSlots: Record<string, string>;
  workspaceBrand: SowWorkspaceBrand;
  visualOverrides: VisualOverrides;
  settings: SowDocumentSettings;
  pageChrome: PageChrome;
  canEdit: boolean;
  /** Off-brand literal heading colors that the "Normalize to brand" action would fix. */
  literalAccentColorCount: number;
  /** The dominant off-brand color (hex, no #) - the candidate to promote to the workspace brand. */
  dominantOffBrandColor: string | null;
  onVisualOverridesChange: (v: VisualOverrides) => void;
  onSettingsChange: (s: SowDocumentSettings) => void;
  onPageChromeChange: (patch: Partial<PageChrome>) => void;
  onNormalizeBrandColors: () => void;
  onPromoteBrandColor: (hex: string) => void;
  onEditData: () => void;
}

export function DocumentInspector(props: DocumentInspectorProps) {
  const [tab, setTab] = React.useState<DocTab>('brand');
  return (
    <div className="sow-studio-inspector__body">
      <div className="sow-studio-inspector__title">
        <span className="sow-studio-inspector__eyebrow">Document</span>
      </div>

      <SegmentedControl<DocTab>
        ariaLabel="Document settings"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'brand', label: 'Brand' },
          { value: 'page', label: 'Page' },
          { value: 'settings', label: 'Settings' },
          { value: 'data', label: 'Data' },
        ]}
      />

      {tab === 'brand' ? (
        <div className="sow-studio-inspector__group">
          <div className="sow-studio-inspector__brand-color">
            <span
              className="sow-studio-inspector__swatch"
              style={{ backgroundColor: `#${props.workspaceBrand.primaryColor ?? '1E3A5F'}` }}
              aria-hidden
            />
            <span className="sow-studio-inspector__value">
              Brand color #{props.workspaceBrand.primaryColor ?? '1E3A5F'}
            </span>
            <Link className="sow-studio-inspector__link" to="/administration/workspace">
              Manage workspace brand
            </Link>
          </div>
          {props.canEdit && props.literalAccentColorCount > 0 ? (
            <div className="sow-studio-inspector__field">
              <p className="sow-studio-inspector__hint">
                {props.literalAccentColorCount} heading color
                {props.literalAccentColorCount === 1 ? '' : 's'} are off-brand (baked from an imported
                doc). Use your workspace brand, or promote the detected color to be the brand.
              </p>
              <div className="sow-studio-inspector__actions">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={props.onNormalizeBrandColors}
                >
                  Use workspace brand
                </Button>
                {props.dominantOffBrandColor &&
                props.dominantOffBrandColor !==
                  (props.workspaceBrand.primaryColor ?? '').toUpperCase() ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => props.onPromoteBrandColor(props.dominantOffBrandColor!)}
                  >
                    Make #{props.dominantOffBrandColor} the brand
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          <SowCoverBrandPanel
            contract={props.draftContract}
            assetSlots={props.assetSlots}
            workspaceBrand={props.workspaceBrand}
            isEditing={props.canEdit}
            overrides={props.visualOverrides}
            onChange={props.onVisualOverridesChange}
          />
        </div>
      ) : null}

      {tab === 'page' ? (
        <PageChromeEditor
          pageChrome={props.pageChrome}
          canEdit={props.canEdit}
          onChange={props.onPageChromeChange}
        />
      ) : null}

      {tab === 'settings' ? (
        <SowDocumentSettingsPanel
          settings={props.settings}
          isEditing={props.canEdit}
          onChange={props.onSettingsChange}
        />
      ) : null}

      {tab === 'data' ? (
        <div className="sow-studio-inspector__group">
          <p className="sow-studio-inspector__hint">
            The preview fills tokens, tables, and AI narratives with representative sample data. Cover
            field values (client name, SOW number, date) come from here too.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={props.onEditData}>
            Edit sample data
          </Button>
        </div>
      ) : null}
    </div>
  );
}
